import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, ArrowRight, BookOpen } from 'lucide-react';
import { usePreferences } from '../context/PreferencesContext';
import { VideoModal, WatchVideoButton } from '../components/common/VideoModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fisher-Yates (Knuth) shuffle algorithm for uniform randomization
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const PracticeSessionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { preferences } = usePreferences();
  const config = location.state?.config;

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answered, setAnswered] = useState(null); // null, true, false
  const [selfScore, setSelfScore] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [questionScores, setQuestionScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoModal, setVideoModal] = useState({ open: false, url: null });

  useEffect(() => {
    if (!config) {
      toast.error('Invalid session configuration');
      navigate('/practice');
      return;
    }

    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const params = {
        grade: preferences.grade,
        mmd_location: preferences.mmd_location,
        function_id: config.function_id
      };

      const response = await axios.get(`${API}/user/questions`, { params });
      
      let filteredQuestions = response.data;
      
      // Filter by topics (support both legacy topic_id and new topic_ids)
      if (config.topic_selection === 'specific' && config.selected_topics.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => {
          // Check topic_ids array first
          if (q.topic_ids?.length > 0) {
            return q.topic_ids.some(tid => config.selected_topics.includes(tid));
          }
          // Fallback to legacy topic_id
          return config.selected_topics.includes(q.topic_id);
        });
      }
      
      // Filter by surveyors
      if (config.selected_surveyors.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => 
          config.selected_surveyors.includes(q.surveyor_internal_id) ||
          config.selected_surveyors.includes(q.surveyor_external_id)
        );
      }
      
      // Filter by asked count
      if (config.asked_filter === 'once') {
        filteredQuestions = filteredQuestions.filter(q => q.asked === 1);
      } else if (config.asked_filter === 'multiple') {
        filteredQuestions = filteredQuestions.filter(q => q.asked > 1);
      }
      
      // Randomize using Fisher-Yates shuffle and limit to num_questions
      const shuffled = shuffleArray(filteredQuestions);
      const selected = shuffled.slice(0, config.num_questions);
      
      setQuestions(selected);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
      navigate('/practice');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswered = (isAnswered) => {
    setAnswered(isAnswered);
    if (!isAnswered) {
      // If not answered, auto-set score to 0
      setSelfScore('0');
    }
  };

  const handleNext = () => {
    if (answered === null) {
      toast.error('Please indicate if you answered the question');
      return;
    }

    if (answered && !selfScore) {
      toast.error('Please enter your self-assessment score');
      return;
    }

    const score = answered ? parseInt(selfScore) || 0 : 0;
    
    if (score < 0 || score > 100) {
      toast.error('Score must be between 0 and 100');
      return;
    }

    // Save score for this question
    const newScores = [...questionScores, {
      question_id: questions[currentIndex].id,
      answered: answered,
      score: score
    }];
    setQuestionScores(newScores);

    // Move to next question or finish
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setAnswered(null);
      setSelfScore('');
      setShowAnswer(false);
    } else {
      // Session complete - save and show results
      finishSession(newScores);
    }
  };

  const finishSession = async (scores) => {
    try {
      const totalScore = scores.reduce((sum, s) => sum + s.score, 0);
      const avgScore = totalScore / scores.length;

      const sessionData = {
        function_id: config.function_id,
        topics: config.selected_topics || [],
        surveyors: config.selected_surveyors || [],
        num_questions: questions.length,
        final_score: parseFloat(avgScore.toFixed(2)),
        question_scores: scores
      };

      await axios.post(`${API}/practice/session`, sessionData);

      // Navigate to results
      navigate('/practice/results', { 
        state: { 
          scores,
          avgScore: parseFloat(avgScore.toFixed(2)),
          config 
        } 
      });
    } catch (error) {
      console.error('Failed to save session:', error);
      toast.error('Failed to save session, but showing results');
      
      // Still show results even if save failed
      const totalScore = scores.reduce((sum, s) => sum + s.score, 0);
      const avgScore = totalScore / scores.length;
      navigate('/practice/results', { 
        state: { 
          scores,
          avgScore: parseFloat(avgScore.toFixed(2)),
          config 
        } 
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12">
          <Card>
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Questions Available</h3>
              <p className="text-gray-500 mb-6">
                No questions found matching your configuration
              </p>
              <Button onClick={() => navigate('/practice')}>
                Back to Configuration
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto">
        {/* Progress Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-700">
              Question {currentIndex + 1} of {questions.length}
            </h2>
            <Badge className="bg-[#8B5CF6]">
              Practice Session
            </Badge>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          <CardContent className="p-4 sm:p-8">
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
              {currentQuestion.question_text}
            </h3>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Badge className="bg-[#0A7EA4]">{preferences.grade}</Badge>
              <Badge className="bg-[#14B8A6]">{preferences.mmd_location}</Badge>
              {currentQuestion.is_decider && (
                <Badge className="bg-red-500">Decider Question</Badge>
              )}
            </div>

            {/* Answer Status Buttons */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Did you answer this question?</p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  onClick={() => handleAnswered(true)}
                  variant={answered === true ? 'default' : 'outline'}
                  className={`flex-1 py-4 sm:py-6 text-base sm:text-lg ${
                    answered === true ? 'bg-green-600 hover:bg-green-700' : ''
                  }`}
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Answered
                </Button>
                <Button
                  onClick={() => handleAnswered(false)}
                  variant={answered === false ? 'default' : 'outline'}
                  className={`flex-1 py-4 sm:py-6 text-base sm:text-lg ${
                    answered === false ? 'bg-red-600 hover:bg-red-700' : ''
                  }`}
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Not Answered
                </Button>
              </div>
            </div>

            {/* Self-Assessment Score */}
            {answered === true && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="text-sm font-medium text-blue-900 mb-2 block">
                  Self-Assessment Score (0-100%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={selfScore}
                  onChange={(e) => setSelfScore(e.target.value)}
                  placeholder="Enter your score (e.g., 85)"
                  className="w-full px-4 py-3 border border-blue-300 rounded-md text-lg focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                />
                <p className="text-xs text-blue-700 mt-2">
                  Compare your answer with the provided answer below and rate how well you did
                </p>
              </div>
            )}

            {/* View Answer Section */}
            <div>
              <Button
                onClick={() => setShowAnswer(!showAnswer)}
                variant="outline"
                className="w-full justify-between mb-3"
              >
                <span>View Correct Answer</span>
                {showAnswer ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>

              {showAnswer && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  {currentQuestion.has_answer ? (
                    <div className="space-y-3">
                      {currentQuestion.answer_text && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-900 mb-1 flex items-center gap-2">
                            Answer:
                            {currentQuestion.video_url && (
                              <WatchVideoButton
                                videoUrl={currentQuestion.video_url}
                                onClick={() => setVideoModal({ open: true, url: currentQuestion.video_url })}
                              />
                            )}
                          </h4>
                          <p className="text-sm text-gray-700">{currentQuestion.answer_text}</p>
                        </div>
                      )}
                      {currentQuestion.explanation && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-900 mb-1">Explanation:</h4>
                          <p className="text-sm text-gray-700">{currentQuestion.explanation}</p>
                        </div>
                      )}
                      {currentQuestion.references && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-900 mb-1">References:</h4>
                          <p className="text-sm text-gray-700">{currentQuestion.references}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">Coming Soon</p>
                      <p className="text-xs text-gray-500 mt-1">Answer is under review</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Next Button */}
        <Button
          onClick={handleNext}
          className="w-full bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 py-6 text-lg"
          disabled={answered === null}
        >
          {currentIndex < questions.length - 1 ? (
            <>
              Next Question
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          ) : (
            <>
              Finish Session
              <CheckCircle className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>

        {/* Explainer Video Modal — YouTube / Dadan iframe */}
        {videoModal.open && (
          <VideoModal
            videoUrl={videoModal.url}
            onClose={() => setVideoModal({ open: false, url: null })}
          />
        )}
      </div>
    </Layout>
  );
};

export default PracticeSessionPage;
